import { PDFDocument, PDFRawStream, decodePDFRawStream, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

const PAGE_WIDTH = 612;  // 8.5" in points
const PAGE_HEIGHT = 792; // 11" in points
const MARGIN = 36;       // 0.5" in points
const LABEL_WIDTH = 122.5;
const LABEL_HEIGHT = 50.5;
const COLS = 4;
const ROWS = 14;
const LABELS_PER_PAGE = COLS * ROWS;
const PLACEHOLDER = "XXXXXXX";
const PLACEHOLDER_GLYPH_WIDTH = 667; // X glyph width in original Arial-BoldMT font (per 1000 units)

function findBytesIndex(haystack: Uint8Array, needle: Uint8Array): number {
    for (let i = 0; i <= haystack.length - needle.length; i++) {
        let match = true;
        for (let j = 0; j < needle.length; j++) {
            if (haystack[i + j] !== needle[j]) {
                match = false;
                break;
            }
        }
        if (match) return i;
    }
    return -1;
}

const NUM = "[\\d.e+\\-]+";
const S = "\\s+";
const CM_RE = new RegExp(`(${NUM})${S}(${NUM})${S}(${NUM})${S}(${NUM})${S}(${NUM})${S}(${NUM})${S}cm`, "g");
const TM_RE = new RegExp(`(${NUM})${S}(${NUM})${S}(${NUM})${S}(${NUM})${S}(${NUM})${S}(${NUM})${S}Tm`);
const TF_RE = /\/[\w+]+\s+([\d.]+)\s+Tf/;

function extractTextPosition(decoded: Uint8Array, placeholderIdx: number): { x: number; y: number; fontSize: number } | null {
    const text = new TextDecoder("latin1").decode(decoded);
    const beforePlaceholder = text.substring(0, placeholderIdx);

    const btIdx = beforePlaceholder.lastIndexOf("BT");
    if (btIdx === -1) return null;

    const beforeBT = beforePlaceholder.substring(0, btIdx);
    const qIdx = beforeBT.lastIndexOf("q ");
    const cmRegion = qIdx !== -1 ? beforeBT.substring(qIdx) : beforeBT;

    let cmA = 1, cmB = 0, cmC = 0, cmD = 1, cmE = 0, cmF = 0;
    const cmMatches = [...cmRegion.matchAll(CM_RE)];
    if (cmMatches.length > 0) {
        const m = cmMatches[cmMatches.length - 1];
        cmA = parseFloat(m[1]); cmB = parseFloat(m[2]);
        cmC = parseFloat(m[3]); cmD = parseFloat(m[4]);
        cmE = parseFloat(m[5]); cmF = parseFloat(m[6]);
    }

    const textBlock = beforePlaceholder.substring(btIdx, placeholderIdx);
    const tmMatch = textBlock.match(TM_RE);
    if (!tmMatch) return null;

    const tmA = parseFloat(tmMatch[1]);
    const tmTx = parseFloat(tmMatch[5]);
    const tmTy = parseFloat(tmMatch[6]);

    const tfMatch = textBlock.match(TF_RE);
    const tfSize = tfMatch ? parseFloat(tfMatch[1]) : 1;

    const fontSize = Math.abs(tmA) * tfSize;
    const x = cmA * tmTx + cmC * tmTy + cmE;
    const y = cmB * tmTx + cmD * tmTy + cmF;

    return { x, y, fontSize };
}

async function findPlaceholderPosition(templateBytes: ArrayBuffer): Promise<{ x: number; y: number; fontSize: number } | null> {
    const searchBytes = new TextEncoder().encode(PLACEHOLDER);
    const doc = await PDFDocument.load(templateBytes, { updateMetadata: false });
    const context = doc.context;
    const result: { x: number; y: number; fontSize: number }[] = [];

    context.enumerateIndirectObjects().forEach(([, obj]) => {
        if (result.length > 0) return;
        if (!(obj instanceof PDFRawStream)) return;
        try {
            const decoded = decodePDFRawStream(obj).decode();
            const idx = findBytesIndex(decoded, searchBytes);
            if (idx === -1) return;
            const pos = extractTextPosition(decoded, idx);
            if (pos) result.push(pos);
        } catch { /* skip */ }
    });

    return result.length > 0 ? result[0] : null;
}

export async function generateLabelPdf(deviceKeys: string[]): Promise<Uint8Array> {
    const [templateBytes, fontBytes] = await Promise.all([
        fetch("/SingleLabel.pdf").then(r => r.arrayBuffer()),
        fetch("/ArialBold.ttf").then(r => r.arrayBuffer()),
    ]);

    const textPos = await findPlaceholderPosition(templateBytes.slice(0));
    if (!textPos) {
        console.warn(`Could not locate ${PLACEHOLDER} in template`);
    }

    const tempDoc = await PDFDocument.load(templateBytes);
    const templatePage = tempDoc.getPage(0);
    const { width: templateWidth, height: templateHeight } = templatePage.getSize();
    const xScale = LABEL_WIDTH / templateWidth;
    const yScale = LABEL_HEIGHT / templateHeight;

    const originalWidth = textPos
        ? PLACEHOLDER.length * PLACEHOLDER_GLYPH_WIDTH / 1000 * textPos.fontSize
        : 0;

    const outputDoc = await PDFDocument.create();
    outputDoc.registerFontkit(fontkit);
    const font = await outputDoc.embedFont(fontBytes);

    const totalPages = Math.ceil(deviceKeys.length / LABELS_PER_PAGE);

    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
        const outputPage = outputDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        const startIdx = pageIdx * LABELS_PER_PAGE;
        const endIdx = Math.min(startIdx + LABELS_PER_PAGE, deviceKeys.length);

        for (let i = startIdx; i < endIdx; i++) {
            const localIdx = i - startIdx;
            const col = localIdx % COLS;
            const row = Math.floor(localIdx / COLS);

            const labelDoc = await PDFDocument.load(templateBytes.slice(0));
            const [embeddedPage] = await outputDoc.embedPdf(labelDoc, [0]);

            const cellX = MARGIN + col * LABEL_WIDTH;
            const cellY = PAGE_HEIGHT - MARGIN - (row + 1) * LABEL_HEIGHT;

            outputPage.drawPage(embeddedPage, { x: cellX, y: cellY, xScale, yScale });

            if (textPos) {
                const outX = cellX + textPos.x * xScale;
                const outY = cellY + textPos.y * yScale;
                const outFontSize = textPos.fontSize * yScale;
                const outWidth = originalWidth * xScale;

                let drawSize = outFontSize;
                if (font.widthOfTextAtSize(deviceKeys[i], drawSize) > outWidth) {
                    drawSize = drawSize * (outWidth / font.widthOfTextAtSize(deviceKeys[i], drawSize));
                }

                outputPage.drawRectangle({
                    x: outX - 1 * xScale,
                    y: outY - 1 * yScale,
                    width: outWidth + 2 * xScale,
                    height: outFontSize * 0.76,
                    color: rgb(1, 1, 1),
                });

                outputPage.drawText(deviceKeys[i], {
                    x: outX,
                    y: outY,
                    size: drawSize,
                    font,
                    color: rgb(0, 0, 0),
                });
            }
        }
    }

    return outputDoc.save();
}

export function downloadPdf(data: Uint8Array, filename: string) {
    const blob = new Blob([data as unknown as BlobPart], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}
